/**
 * In-memory mock API for local workshop demos (VITE_USE_MOCK=true).
 * Reset on full page reload.
 */

import {
  ADMIN_TEST_UNIVERSITY,
  canSeeAdminTestUniversity,
  isAdminTestUniversity,
  isEverydaySession,
  todayUtcIso,
} from './adminTestUniversity.js'

const store = {
  users: [
    {
      sub: 'mock-student-sub',
      email: 'student@example.com',
      name: 'Demo Student',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-pending-sub',
      email: 'pending@example.com',
      name: 'Pending Student',
      status: 'pending',
      createdAt: '2026-01-02T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-removed-sub',
      email: 'removed@example.com',
      name: 'Removed Student',
      status: 'removed',
      createdAt: '2026-01-03T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-admin-sub',
      email: 'admin@example.com',
      name: 'Facilitator Admin',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
  ],
  colleges: [
    {
      id: 'demo-stanford-2026',
      name: 'Stanford University',
      workshopDate: '2026-04-15',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-mit-2026',
      name: 'MIT',
      workshopDate: '2026-12-01',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-unscheduled',
      name: 'Reed College',
      workshopDate: '',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-disabled',
      name: 'Disabled College Demo',
      workshopDate: '2026-06-01',
      enabled: false,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
  ],
  requests: [],
  votes: {}, // `${collegeId}:${slideId}:${sub}` -> choiceId | choiceIds[] (multi-select)
  aggregates: {}, // `${collegeId}:${slideId}` -> { choiceId: count }
  progress: {}, // sub -> { lastSlideId, updatedAt }
}

const ADMIN_EMAILS = new Set([
  'prashanth.ayyavu@gmail.com',
  'codeshipwatch@gmail.com',
  'silvia.r.muthumani@gmail.com',
  'admin@example.com',
])

function isAdminEmail(email) {
  return ADMIN_EMAILS.has((email || '').trim().toLowerCase())
}

function canSeeCollege(session, name) {
  if (!isAdminTestUniversity(name)) return true
  return canSeeAdminTestUniversity(session?.email, { isAdmin: !!session?.isAdmin })
}

function ensureAdminTestUniversity() {
  const existing = store.colleges.find((c) => isAdminTestUniversity(c.name))
  if (existing) {
    existing.everyday = true
    existing.workshopDate = ''
    existing.enabled = true
    return
  }
  store.colleges.push({
    id: 'admin-test-university',
    name: ADMIN_TEST_UNIVERSITY,
    workshopDate: '',
    everyday: true,
    enabled: true,
    createdAt: now(),
    updatedAt: now(),
  })
}

function now() {
  return new Date().toISOString()
}

function ensureUser(session) {
  let user = store.users.find((u) => u.sub === session.sub)
  if (!user) {
    user = {
      sub: session.sub,
      email: session.email,
      name: session.name,
      picture: session.picture,
      status: session.isAdmin ? 'approved' : 'pending',
      createdAt: now(),
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    }
    store.users.push(user)
  }
  return user
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function yesterdayIso() {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

function validDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : ''
}

function workshopStateForDate(workshopDate) {
  if (!workshopDate) return 'unscheduled'
  const today = todayIso()
  if (workshopDate > today) return 'upcoming'
  if (workshopDate >= yesterdayIso()) return 'today'
  return 'past'
}

function resolveWorkshop(name, { enabledOnly = true } = {}) {
  const key = (name || '').trim().toLowerCase()
  if (!key) return null
  let matches = store.colleges.filter((c) => c.name.toLowerCase() === key)
  if (enabledOnly) {
    const enabled = matches.filter((c) => c.enabled)
    if (enabled.length) matches = enabled
  }
  if (!matches.length) return null
  const everyday = matches.find((c) => isEverydaySession(c))
  if (everyday) return everyday
  const today = todayIso()
  const floor = yesterdayIso()
  const dated = matches.filter((c) => validDate(c.workshopDate))
  const live = dated
    .filter((c) => c.workshopDate >= floor && c.workshopDate <= today)
    .sort((a, b) => b.workshopDate.localeCompare(a.workshopDate))
  if (live.length) return live[0]
  const upcoming = dated
    .filter((c) => c.workshopDate > today)
    .sort((a, b) => a.workshopDate.localeCompare(b.workshopDate))
  if (upcoming.length) return upcoming[0]
  const past = dated
    .filter((c) => c.workshopDate < floor)
    .sort((a, b) => b.workshopDate.localeCompare(a.workshopDate))
  if (past.length) return past[0]
  return matches[0]
}

function findCollegeForStudent(collegeId, collegeName, session) {
  const isAdmin = !!session?.isAdmin
  let college = null
  if (collegeId) {
    const found = store.colleges.find((c) => c.id === collegeId)
    if (found && (found.enabled || isAdmin)) college = found
  } else {
    college = resolveWorkshop(collegeName, { enabledOnly: !isAdmin })
  }
  if (college && !canSeeCollege(session, college.name)) return null
  return college
}

function findSession(collegeName, workshopDate) {
  const date = validDate(workshopDate)
  const key = (collegeName || '').trim().toLowerCase()
  if (!key || !date) return null
  return (
    store.colleges.find(
      (c) => c.name.toLowerCase() === key && validDate(c.workshopDate) === date,
    ) || null
  )
}

function mePayload(session, user) {
  if (session.isAdmin) {
    return {
      sub: user.sub,
      email: user.email,
      name: user.name,
      picture: user.picture || null,
      status: 'approved',
      isAdmin: true,
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      workshopState: null,
      sessionEnabled: false,
      assigned: false,
      everyday: false,
      requestedCollegeName: null,
    }
  }
  let assigned = user.collegeId
    ? store.colleges.find((c) => c.id === user.collegeId)
    : null
  if (assigned && !canSeeCollege(session, assigned.name)) assigned = null
  if (
    !assigned &&
    user.status === 'approved' &&
    isAdminTestUniversity(user.collegeName)
  ) {
    const rebound = store.colleges.find(
      (c) => isEverydaySession(c) && isAdminTestUniversity(c.name),
    )
    if (rebound && canSeeCollege(session, rebound.name)) assigned = rebound
  }
  let collegeId = null
  let collegeName = user.collegeName || null
  let workshopDate = null
  let workshopState = null
  let sessionEnabled = false
  let bound = false
  if (assigned && isEverydaySession(assigned)) {
    collegeId = assigned.id
    collegeName = assigned.name
    workshopDate = todayUtcIso()
    workshopState = 'today'
    sessionEnabled = !!assigned.enabled
    bound = true
  } else if (assigned) {
    collegeId = assigned.id
    collegeName = assigned.name
    workshopDate = validDate(assigned.workshopDate) || null
    workshopState = workshopStateForDate(workshopDate || '')
    sessionEnabled = !!assigned.enabled
    bound = true
  } else if (user.requestedCollegeName && !user.collegeName) {
    collegeName = null
    workshopState = null
  } else if (user.collegeName && !canSeeCollege(session, user.collegeName)) {
    collegeName = null
    workshopState = null
  } else if (user.collegeName) {
    const preview = resolveWorkshop(user.collegeName)
    collegeName = user.collegeName
    if (preview && isEverydaySession(preview)) {
      workshopDate = todayUtcIso()
      workshopState = 'today'
      sessionEnabled = !!preview.enabled
    } else {
      workshopDate = preview ? validDate(preview.workshopDate) || null : null
      workshopState = workshopStateForDate(workshopDate || '')
      sessionEnabled = !!(preview && preview.enabled)
    }
  }
  return {
    sub: user.sub,
    email: user.email,
    name: user.name,
    picture: user.picture || null,
    status: session.isAdmin ? 'approved' : user.status,
    isAdmin: !!session.isAdmin,
    collegeId,
    collegeName,
    workshopDate,
    workshopState,
    sessionEnabled,
    assigned: bound,
    everyday: isAdminTestUniversity(collegeName),
    requestedCollegeName: user.requestedCollegeName || null,
  }
}

function forbidden() {
  const err = new Error('Forbidden')
  err.status = 403
  throw err
}

function voteKey(collegeId, slideId, sub) {
  return `${collegeId}:${slideId}:${sub}`
}

function aggKey(collegeId, slideId) {
  return `${collegeId}:${slideId}`
}

function datedSession(collegeId) {
  const row = store.colleges.find((c) => c.id === collegeId)
  if (!row) return null
  if (isEverydaySession(row)) return row
  if (!validDate(row.workshopDate)) return null
  return row
}

export async function getMe(session) {
  const user = ensureUser(session)
  if (user.requestedCollegeName) {
    user.requestedCollegeName = null
  }
  if (session.isAdmin) {
    user.collegeId = null
    user.collegeName = null
    user.workshopDate = null
    user.requestedCollegeName = null
  }
  ensureAdminTestUniversity()
  const payload = mePayload(session, user)
  if (
    !session.isAdmin &&
    user.status === 'approved' &&
    payload.collegeId &&
    user.collegeId !== payload.collegeId
  ) {
    user.collegeId = payload.collegeId
    user.collegeName = payload.collegeName
    if (payload.everyday) user.workshopDate = null
  }
  return payload
}

export async function setMyCollege(session, collegeIdOrOpts, collegeNameArg) {
  ensureAdminTestUniversity()
  const user = ensureUser(session)
  const opts =
    collegeIdOrOpts && typeof collegeIdOrOpts === 'object'
      ? collegeIdOrOpts
      : { collegeId: collegeIdOrOpts, collegeName: collegeNameArg }

  if (session.isAdmin) {
    const err = new Error('Admins are not assigned to a college')
    err.status = 400
    throw err
  }

  const name = (opts.collegeName || '').trim()
  if (!canSeeCollege(session, name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  const matches = store.colleges.filter((c) => c.name.toLowerCase() === name.toLowerCase())
  if (!name || !matches.length) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  const chosen = matches[0].name
  const sameCollege = (user.collegeName || '').toLowerCase() === chosen.toLowerCase()
  user.collegeName = chosen
  user.requestedCollegeName = null
  if (user.status === 'approved' && (sameCollege || isAdminTestUniversity(chosen))) {
    const row = isAdminTestUniversity(chosen)
      ? store.colleges.find((c) => isEverydaySession(c) && isAdminTestUniversity(c.name))
      : store.colleges.find((c) => c.id === user.collegeId)
    if (row && (row.name || '').toLowerCase() === chosen.toLowerCase()) {
      user.collegeId = row.id
      user.workshopDate = isEverydaySession(row) ? null : validDate(row.workshopDate)
    } else {
      user.collegeId = null
      user.workshopDate = null
    }
  } else {
    user.collegeId = null
    user.workshopDate = null
  }
  if (user.status === 'removed') {
    user.status = 'pending'
  }
  return getMe(session)
}

export async function requestCollege(session, collegeName) {
  const name = (collegeName || '').trim()
  if (!name) {
    const err = new Error('collegeName is required')
    err.status = 400
    throw err
  }
  const existing = findCollegeForStudent('', name, session)
  if (existing) {
    return setMyCollege(session, { collegeName: name })
  }
  const user = ensureUser(session)
  store.requests.push({
    id: `r-${Math.random().toString(36).slice(2, 10)}`,
    collegeName: name,
    sub: user.sub,
    email: user.email,
    name: user.name,
    createdAt: now(),
  })
  user.requestedCollegeName = null
  user.collegeId = null
  user.collegeName = null
  user.workshopDate = null
  if (user.status === 'removed') {
    user.status = 'pending'
  }
  return getMe(session)
}

export async function checkAdmin(session) {
  return { isAdmin: !!session.isAdmin }
}

export async function listColleges(session, { q, all } = {}) {
  ensureAdminTestUniversity()
  const query = (q || '').trim().toLowerCase()
  const seen = new Map()
  for (const c of store.colleges) {
    if (!(all && session.isAdmin) && !c.enabled) continue
    if (!canSeeCollege(session, c.name)) continue
    if (query && !c.name.toLowerCase().includes(query)) continue
    const key = c.name.toLowerCase()
    if (!seen.has(key)) seen.set(key, { name: c.name })
  }
  const colleges = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  return { colleges }
}

export async function adminListColleges(session) {
  if (!session.isAdmin) forbidden()
  ensureAdminTestUniversity()
  const colleges = store.colleges
    .filter((c) => canSeeCollege(session, c.name))
    .sort((a, b) =>
      `${a.workshopDate}${a.name}`.localeCompare(`${b.workshopDate}${b.name}`),
    )
  return { colleges }
}

export async function adminListCollegeRequests(session) {
  if (!session.isAdmin) forbidden()
  const requests = [...store.requests].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt)),
  )
  return { requests }
}

export async function adminCreateCollege(session, { name, workshopDate, enabled = true }) {
  if (!session.isAdmin) forbidden()
  const n = (name || '').trim()
  const d = (workshopDate || '').trim()
  if (n && !canSeeCollege(session, n)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  if (isAdminTestUniversity(n)) {
    const existingAtu = store.colleges.find((c) => isAdminTestUniversity(c.name))
    if (existingAtu) {
      const err = new Error('A college with this name and date already exists')
      err.status = 409
      throw err
    }
  }
  if (!n) {
    const err = new Error('name is required')
    err.status = 400
    throw err
  }
  const everyday = isAdminTestUniversity(n)
  if (!everyday && !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const err = new Error('workshopDate must be YYYY-MM-DD')
    err.status = 400
    throw err
  }
  const date = everyday ? '' : d
  const dup = store.colleges.find(
    (c) => c.name.toLowerCase() === n.toLowerCase() && c.workshopDate === date,
  )
  if (dup) {
    const err = new Error('A college with this name and date already exists')
    err.status = 409
    throw err
  }
  const college = {
    id: `c-${Math.random().toString(36).slice(2, 10)}`,
    name: n,
    workshopDate: date,
    everyday,
    enabled: !!enabled,
    createdAt: now(),
    updatedAt: now(),
  }
  store.colleges.push(college)
  return { college }
}

export async function adminUpdateCollege(session, id, patch) {
  if (!session.isAdmin) forbidden()
  const college = store.colleges.find((c) => c.id === id)
  if (!college || !canSeeCollege(session, college.name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  if (patch.name !== undefined) {
    const n = String(patch.name).trim()
    if (!n) {
      const err = new Error('name cannot be empty')
      err.status = 400
      throw err
    }
    if (!canSeeCollege(session, n)) {
      const err = new Error('College not found')
      err.status = 404
      throw err
    }
    college.name = n
  }
  if (patch.workshopDate !== undefined) {
    const d = String(patch.workshopDate || '').trim()
    if (d && !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const err = new Error('workshopDate must be YYYY-MM-DD')
      err.status = 400
      throw err
    }
    college.workshopDate = d
  }
  if (patch.enabled !== undefined) {
    college.enabled = !!patch.enabled
  }
  const dup = store.colleges.find(
    (c) =>
      c.id !== id &&
      c.name.toLowerCase() === college.name.toLowerCase() &&
      c.workshopDate === college.workshopDate,
  )
  if (dup) {
    const err = new Error('A college with this name and date already exists')
    err.status = 409
    throw err
  }
  college.updatedAt = now()
  return { college: { ...college } }
}

export async function adminDeleteCollege(session, id) {
  if (!session.isAdmin) forbidden()
  const idx = store.colleges.findIndex((c) => c.id === id)
  if (idx < 0 || !canSeeCollege(session, store.colleges[idx].name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  store.colleges.splice(idx, 1)
  return { deleted: true, id }
}

function userPublic(user) {
  const admin = isAdminEmail(user.email)
  return {
    ...user,
    isAdmin: admin,
    collegeId: admin ? null : user.collegeId,
    collegeName: admin ? null : user.collegeName,
    workshopDate: admin ? null : user.workshopDate,
    requestedCollegeName: admin ? null : user.requestedCollegeName,
  }
}

export async function listUsers(session, { status } = {}) {
  if (!session.isAdmin) forbidden()
  let users = store.users.map(userPublic)
  if (status && status !== 'all') {
    users = users.filter((u) => u.status === status)
  }
  if (!status || status === 'all') {
    const seen = new Set(users.map((u) => (u.email || '').toLowerCase()))
    for (const email of [...ADMIN_EMAILS].sort()) {
      if (seen.has(email)) continue
      users.push({
        sub: '',
        email,
        name: '',
        status: 'admin',
        isAdmin: true,
        collegeId: null,
        collegeName: null,
        workshopDate: null,
        requestedCollegeName: null,
      })
    }
  }
  return { users, status: status || 'pending' }
}

export async function approveUser(session, sub, { sessionId, collegeId, workshopDate, collegeName } = {}) {
  if (!session.isAdmin) forbidden()
  const user = store.users.find((u) => u.sub === sub)
  if (!user) {
    const err = new Error('Not found')
    err.status = 404
    throw err
  }
  if (isAdminEmail(user.email)) {
    const err = new Error('Admins are not assigned to a college')
    err.status = 400
    throw err
  }
  const name = collegeName || user.collegeName || user.requestedCollegeName
  let row =
    store.colleges.find((c) => c.id === (sessionId || collegeId)) ||
    findSession(name, workshopDate)
  if (!row && isAdminTestUniversity(name)) {
    row = store.colleges.find((c) => isAdminTestUniversity(c.name)) || null
  }
  if (row && !canSeeCollege(session, row.name)) {
    const err = new Error('College not found')
    err.status = 404
    throw err
  }
  if (row && !canSeeCollege({ email: user.email }, row.name)) {
    const err = new Error('This university is limited to specific accounts')
    err.status = 400
    throw err
  }
  if (!row) {
    const err = new Error('Approve the student against a workshop date')
    err.status = 400
    throw err
  }
  user.status = 'approved'
  user.approvedBy = session.sub
  user.approvedAt = now()
  user.collegeId = row.id
  user.collegeName = row.name
  user.workshopDate = isEverydaySession(row) ? null : validDate(row.workshopDate)
  user.requestedCollegeName = null
  return { user }
}

export async function removeUser(session, sub) {
  if (!session.isAdmin) forbidden()
  const user = store.users.find((u) => u.sub === sub)
  if (!user) {
    const err = new Error('Not found')
    err.status = 404
    throw err
  }
  if (isAdminEmail(user.email)) {
    const err = new Error('Admins are not assigned to a college')
    err.status = 400
    throw err
  }
  user.status = 'removed'
  // Drop the session so the user signs up again (college name).
  user.collegeId = null
  user.collegeName = null
  user.workshopDate = null
  user.requestedCollegeName = null
  user.approvedBy = session.sub
  user.approvedAt = now()
  return { user }
}

// `choice` is a single choiceId or an array of choiceIds (multi-select polls).
// Each selected choice counts once toward the aggregate; a user still votes once.
export async function vote(session, slideId, choice) {
  const user = ensureUser(session)
  const row = datedSession(user.collegeId)
  if (!row) {
    const err = new Error('Select a college and workshop date before voting')
    err.status = 400
    throw err
  }
  const key = voteKey(row.id, slideId, session.sub)
  if (store.votes[key]) {
    const err = new Error('Already voted')
    err.status = 409
    throw err
  }
  const isMulti = Array.isArray(choice)
  const choiceIds = isMulti ? [...new Set(choice)] : [choice]
  store.votes[key] = isMulti ? choiceIds : choice
  const ak = aggKey(row.id, slideId)
  if (!store.aggregates[ak]) {
    store.aggregates[ak] = {}
  }
  for (const cid of choiceIds) {
    store.aggregates[ak][cid] = (store.aggregates[ak][cid] || 0) + 1
  }
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    workshopDate: validDate(row.workshopDate),
    ...(isMulti ? { choiceIds } : { choiceId: choice }),
    votedAt: now(),
  }
}

export async function getMyVote(session, slideId) {
  const user = ensureUser(session)
  const row = datedSession(user.collegeId)
  if (!row) {
    return { vote: null }
  }
  const key = voteKey(row.id, slideId, session.sub)
  const val = store.votes[key]
  if (!val) {
    return { slideId, collegeId: row.id, vote: null }
  }
  const vote = Array.isArray(val)
    ? { slideId, choiceIds: val }
    : { slideId, choiceId: val }
  return { slideId, collegeId: row.id, vote }
}

export async function getPollResults(session, slideId, { collegeId, collegeName, workshopDate } = {}) {
  if (!session.isAdmin) forbidden()
  let row = datedSession(collegeId)
  if (!row && isAdminTestUniversity(collegeName)) {
    row = store.colleges.find((c) => isAdminTestUniversity(c.name)) || null
  }
  if (!row && collegeName && workshopDate) {
    row = findSession(collegeName, workshopDate)
    if (row && !isEverydaySession(row) && !validDate(row.workshopDate)) row = null
  }
  if (row && !canSeeCollege(session, row.name)) row = null
  if (!row) {
    const err = new Error('Select a college and workshop date to view results')
    err.status = 400
    throw err
  }
  const counts = store.aggregates[aggKey(row.id, slideId)] || {}
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    workshopDate: validDate(row.workshopDate),
    counts,
    total,
  }
}

export async function clearPollResults(session, slideId, { collegeId, collegeName, workshopDate } = {}) {
  if (!session.isAdmin) forbidden()
  let row = datedSession(collegeId)
  if (!row && isAdminTestUniversity(collegeName)) {
    row = store.colleges.find((c) => isAdminTestUniversity(c.name)) || null
  }
  if (!row && collegeName && workshopDate) {
    row = findSession(collegeName, workshopDate)
    if (row && !isEverydaySession(row) && !validDate(row.workshopDate)) row = null
  }
  if (row && !canSeeCollege(session, row.name)) row = null
  if (!row) {
    const err = new Error('Select a college and workshop date to clear results')
    err.status = 400
    throw err
  }
  const prefix = `${row.id}:${slideId}`
  let deleted = 0
  for (const key of Object.keys(store.votes)) {
    if (key.startsWith(`${prefix}:`)) {
      delete store.votes[key]
      deleted += 1
    }
  }
  const ak = aggKey(row.id, slideId)
  if (store.aggregates[ak]) {
    delete store.aggregates[ak]
    deleted += 1
  }
  return {
    slideId,
    collegeId: row.id,
    collegeName: row.name,
    workshopDate: validDate(row.workshopDate),
    deleted,
    counts: {},
    total: 0,
  }
}

export async function getProgress(session) {
  return store.progress[session.sub] || { lastSlideId: null, updatedAt: null }
}

export async function putProgress(session, lastSlideId) {
  store.progress[session.sub] = { lastSlideId, updatedAt: now() }
  return store.progress[session.sub]
}

/** Test helper — wipe in-memory state */
export function __resetStore() {
  store.users = [
    {
      sub: 'mock-student-sub',
      email: 'student@example.com',
      name: 'Demo Student',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-pending-sub',
      email: 'pending@example.com',
      name: 'Pending Student',
      status: 'pending',
      createdAt: '2026-01-02T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-removed-sub',
      email: 'removed@example.com',
      name: 'Removed Student',
      status: 'removed',
      createdAt: '2026-01-03T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
    {
      sub: 'mock-admin-sub',
      email: 'admin@example.com',
      name: 'Facilitator Admin',
      status: 'approved',
      createdAt: '2026-01-01T00:00:00.000Z',
      collegeId: null,
      collegeName: null,
      workshopDate: null,
      requestedCollegeName: null,
    },
  ]
  store.colleges = [
    {
      id: 'demo-stanford-2026',
      name: 'Stanford University',
      workshopDate: '2026-04-15',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-mit-2026',
      name: 'MIT',
      workshopDate: '2026-12-01',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-unscheduled',
      name: 'Reed College',
      workshopDate: '',
      enabled: true,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
    {
      id: 'demo-disabled',
      name: 'Disabled College Demo',
      workshopDate: '2026-06-01',
      enabled: false,
      createdAt: '2026-01-01T00:00:00.000Z',
      updatedAt: '2026-01-01T00:00:00.000Z',
    },
  ]
  store.requests = []
  store.votes = {}
  store.aggregates = {}
  store.progress = {}
}

export function __getStore() {
  return store
}
